import { useQuery } from "@apollo/client"
import { Box, Button, HStack, VStack } from "@chakra-ui/react"
import { CloudArrowsFillIcon, CloudFillIcon } from "@pathwright/pathicons"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import {
  AUGMENTATION_PANEL_QUERY_PARAM,
  getGroupMessageUrl,
  getGroupUrl,
  getManageGroupUrl,
  getManageLicenseOfferingUrl,
  getManageResourceUrl,
  getStoreResourceUrl
} from "@pathwright/web/src/modules/utils/urls"
import gql from "graphql-tag"
import path from "path-browserify"
import { Motion, spring } from "react-motion"
import { flattenEdges } from "../../utils/apollo"
import BackSyncItem from "./BackSyncItem"
import CardTemplate, { CardTemplateProps } from "./CardTemplate"
import SuggestedAction from "./SuggestedAction"
import { useSyncCard } from "./SyncCardProvider"
import { FromCohort } from "./types"
import { useCanDesignSourcePath, useShowSyncOption } from "./utils"

// Link directly to the card, stripping out current "/sync" segment.
// There's some wonky card routing that occurs otherwise when navigating
// the card stack.
function getCardTo(to: string) {
  // Temoporarily disable ?panel route until we fix broken issues.
  return to

  return path.join(
    window.location.pathname,
    "/..",
    `?${AUGMENTATION_PANEL_QUERY_PARAM}=${to}`
  )
}

const CohortInvitationsQuery = gql`
  query invitations($cohort_id: Int!) {
    invitations(first: 1, type: cohort, context: { cohort_id: $cohort_id }) {
      total
    }
  }
`

const SuccessCard = (props: CardTemplateProps) => {
  const { fromCohort } = useSyncCard()

  const resource = fromCohort?.resource

  if (!fromCohort || !resource) return null

  return (
    <Motion
      defaultStyle={{ x: 0 }}
      style={{
        x: spring(1, {
          stiffness: 300,
          damping: 20
        })
      }}
    >
      {(interpolated: any) => {
        const style = {
          opacity: interpolated.x,
          transformOrigin: "50% 50%",
          transform: `scale(${interpolated.x})`
        }

        return (
          <Box
            sx={{
              ".SyncCard__card-wrapper": {
                backgroundColor: "transparent"
              },
              ".chakra-heading, div, p, .SuggestedAction > button": {
                color: "rgba(255, 255, 255, 0.9)"
              },
              ".PathPublishCard__item": {
                backgroundColor: "white"
              },
              ".chakra-accordion__button:hover .chakra-button": {
                color: "rgba(255, 255, 255, 0.9)"
              },
              ".chakra-accordion__button:not([aria-expanded=true]):hover": {
                backgroundColor: "transparent"
              },
              "a.Link": {
                textDecoration: "none"
              },
              "svg, .SuggestedAction svg": {
                color: "var(--chakra-colors-textOnDark)",
                fill: "var(--chakra-colors-textOnDark)"
              }
            }}
            _hover={{
              "svg, .SuggestedAction svg": {
                color: "rgba(255, 255, 255, 0.9)",
                fill: "rgba(255, 255, 255, 0.9)"
              }
            }}
            w="full"
            style={style}
            bgRepeat="no-repeat"
            bgSize="cover"
            borderRadius="card"
            bg="#444"
          >
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              overflow="hidden"
              borderRadius="card"
            >
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                bgPos="center bottom"
                bgRepeat="no-repeat"
                bgSize="cover"
                filter="auto"
                blur="70px"
                brightness="0.6"
                bgImage={`url(${resource.background_image})`}
              />
            </Box>
            <CardTemplate {...props} />
          </Box>
        )
      }}
    </Motion>
  )
}

export const SyncSuccess = () => {
  const { fromCohort, onCreateCohort, cohortName } = useSyncCard()

  const { tc } = useTranslate()

  const resource = fromCohort?.resource

  if (!fromCohort || !resource) return null

  return (
    <SuccessCard
      icon="cloud-arrows-fill"
      title={tc("Synced")}
      description={tc(
        "All changes to {{resourceName}} > {{cohortName}} are synced with the Source Path.",
        {
          resourceName: resource.name,
          cohortName: cohortName
        }
      )}
      renderBody={() => (
        <VStack gap={"syncMd"} w="full">
          {onCreateCohort ? (
            <SuggestedAction
              label={tc("Create a new Cohort")}
              onClick={onCreateCohort}
              icon="group-plus"
            />
          ) : null}
          <SuggestedAction
            label={tc("Enable anyone to create a Cohort")}
            url={getCardTo(getManageLicenseOfferingUrl(resource.id))}
            icon="group"
          />
        </VStack>
      )}
      renderButton={() => (
        <a href={getGroupUrl(resource.slug, fromCohort.source?.id!)}>
          <Button
            colorScheme="brand"
            cursor="pointer"
            sx={{
              ".chakra-button__icon": {
                marginRight: "4px"
              },
              svg: {
                marginBottom: "-4px"
              }
            }}
            leftIcon={<CloudArrowsFillIcon color="white" size="1.2em" />}
          >
            {tc("View Source Path")}
          </Button>
        </a>
      )}
      tipId="path-sync-success"
    />
  )
}

export const SourcePublishSuccess = () => {
  const { fromCohort, onClose, cohortName } = useSyncCard()

  const { tc } = useTranslate()

  const resource = fromCohort.resource

  if (!fromCohort || !resource) return null

  const { cohorts } = resource
  const cohortItems = flattenEdges(cohorts)

  return (
    <SuccessCard
      icon="cloud-fill"
      title={tc("Published")}
      description={tc(
        "All changes in the Source Path are available to all {{cohortCount}} Cohorts:",
        {
          cohortCount: cohorts?.total
        }
      )}
      renderBody={() => (
        <VStack py={"syncMd"} gap={2} bg="transparent" w="full" align="center">
          {cohortItems.slice(0, 6).map((cohort: FromCohort, index: number) => {
            if (index === 5) {
              return (
                <Box pt={3}>
                  <SuggestedAction
                    key={`view-all-${fromCohort.id}`}
                    label={tc("View all {{cohortCount}} Cohorts", {
                      cohortCount: cohorts?.total
                    })}
                    url={getCardTo(
                      `${getManageResourceUrl(resource.id)}cohorts/manage/`
                    )}
                    icon="group"
                  />
                </Box>
              )
            }

            return (
              <SuggestedAction
                key={`source-success-cohort-${cohort.id}`}
                label={cohortName!}
                url={getGroupUrl(resource.slug, cohort.id)}
                icon="group"
              />
            )
          })}
        </VStack>
      )}
      renderButton={() => (
        <Button
          colorScheme="brand"
          cursor="pointer"
          onClick={onClose}
          sx={{
            ".chakra-button__icon": {
              marginRight: "4px"
            },
            svg: {
              marginBottom: "-4px"
            }
          }}
          leftIcon={<CloudArrowsFillIcon color="white" size="1.2em" />}
        >
          {tc("View Source Path")}
        </Button>
      )}
      renderTip={() => null}
    />
  )
}

export const PublishSuccess = () => {
  const { fromCohort, onClose, cohortName } = useSyncCard()
  const canDesignSourcePath = useCanDesignSourcePath({
    cohortId: fromCohort.id
  })
  const showSyncOption = useShowSyncOption({
    cohortId: fromCohort.id
  })

  const { tc } = useTranslate()

  const cohortInvitationsQuery = useQuery(CohortInvitationsQuery, {
    variables: {
      cohort_id: fromCohort?.id
    },
    skip: !fromCohort?.id
  })

  const hasLearners = (fromCohort?.registrations?.total || 0) > 0
  const hasInvitations = !!cohortInvitationsQuery?.data?.invitations?.total

  const resource = fromCohort?.resource

  if (!fromCohort || !resource) return null

  return (
    <SuccessCard
      icon="cloud-fill"
      title={tc("Published")}
      description={tc(
        "All changes to {{resourceName}} > {{cohortName}} are available to members.",
        {
          resourceName: resource.name,
          cohortName
        }
      )}
      renderBody={() => (
        <VStack py={"syncMd"} gap={0} bg="transparent" w="full" align="start">
          {hasLearners ? (
            <>
              <VStack gap={"syncMd"} w="full">
                <SuggestedAction
                  label={tc("Send a message to everyone here")}
                  url={getGroupMessageUrl(resource.slug, fromCohort.id, "path")}
                  icon="text-edit"
                />
                {canDesignSourcePath &&
                (!resource.description && !resource.short_description) ? (
                  <SuggestedAction
                    label={tc("Update Library page")}
                    url={getStoreResourceUrl(resource.slug)}
                    icon="edit"
                  />
                ) : null}
              </VStack>
            </>
          ) : (
            <>
              <VStack gap={"syncMd"} w="full">
                {!hasInvitations && (
                  <SuggestedAction
                    icon="group-plus"
                    label={tc("Invite or add people")}
                    url={`${getGroupUrl(
                      resource.slug,
                      fromCohort.id
                    )}?share=true`}
                  />
                )}
                {canDesignSourcePath &&
                (!resource.description && !resource.short_description) ? (
                  <SuggestedAction
                    label={tc("Update Library page")}
                    url={getStoreResourceUrl(resource.slug)}
                    icon="edit"
                  />
                ) : null}
                {canDesignSourcePath && (
                  <SuggestedAction
                    label={tc("Configure access, pricing & more...")}
                    url={getCardTo(
                      getManageGroupUrl(resource.id, fromCohort.id)
                    )}
                    icon="gear"
                  />
                )}
              </VStack>
            </>
          )}
          {showSyncOption ? (
            <HStack justify="center" py="syncMd" w="full">
              <BackSyncItem />
            </HStack>
          ) : null}
        </VStack>
      )}
      renderButton={() => (
        <Button
          colorScheme="brand"
          cursor="pointer"
          onClick={onClose}
          sx={{
            ".chakra-button__icon": {
              marginRight: "4px"
            },
            svg: {
              marginBottom: "-4px"
            }
          }}
          leftIcon={<CloudFillIcon color="white" size="1.2em" />}
        >
          {tc("View Path")}
        </Button>
      )}
      tipId="path-publish-success"
    />
  )
}
